import { queryOptions, type QueryClient } from '@tanstack/react-query'
import { api, type User } from '../api/client'

// Session state lives in TanStack Query so routes, the sidebar and forms
// all read the same cache. The server is the source of truth: the cookie is
// httpOnly, so "am I signed in" is answered by GET /api/me.

export const meQuery = queryOptions({
  queryKey: ['me'],
  queryFn: async (): Promise<User | null> => {
    const { data, response } = await api.GET('/me')
    if (response.status === 401) return null
    if (!data) throw new Error(`GET /me failed: ${response.status}`)
    return data
  },
  staleTime: 60_000,
})

export const setupStatusQuery = queryOptions({
  queryKey: ['setup-status'],
  queryFn: async () => {
    const { data, response } = await api.GET('/setup')
    if (!data) throw new Error(`GET /setup failed: ${response.status}`)
    return data
  },
  staleTime: 60_000,
})

export interface Auth {
  /** Resolve the current user (cached), or null when signed out. */
  me(): Promise<User | null>
  /** Whether first-run setup is still required. */
  setupNeeded(): Promise<boolean>
  /** Forget cached session state after login/logout/setup. */
  invalidate(): Promise<void>
  logout(): Promise<void>
}

export function createAuth(queryClient: QueryClient): Auth {
  return {
    me: () => queryClient.ensureQueryData(meQuery),
    setupNeeded: async () => (await queryClient.ensureQueryData(setupStatusQuery)).needed,
    // Drop rather than invalidate: ensureQueryData hands back stale cache
    // entries, which would let the setup/login guards redirect on old state.
    invalidate: async () => {
      queryClient.removeQueries({ queryKey: meQuery.queryKey })
      queryClient.removeQueries({ queryKey: setupStatusQuery.queryKey })
    },
    logout: async () => {
      // DIS-27 implements POST /auth/logout; until then this clears local state.
      await api.POST('/auth/logout').catch(() => undefined)
      queryClient.removeQueries({ queryKey: meQuery.queryKey })
    },
  }
}
